import { Badge, cn } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import type { PortalTicket } from "../../../lib/portal";
import { fmtDateTime, genericStatusVariant } from "../../../lib/format";
import { RowButton } from "../../ui";

/**
 * The conversations, newest activity first as the server sends them.
 *
 * The open one is marked three ways - a tint, a dot and a heavier subject - and
 * says so to a screen reader with `aria-current`. A selected row that differed
 * only by a faint background would be invisible to anybody who cannot see the
 * difference between two near-whites.
 */
export function TicketList({
  tickets,
  activeId,
  onOpen,
}: {
  tickets: PortalTicket[];
  activeId: string | null;
  onOpen: (id: string) => void;
}) {
  const t = useT();
  const { locale } = useI18n();

  return (
    <ul className="divide-y divide-border">
      {tickets.map((tk) => {
        const on = tk.id === activeId;
        return (
          <li key={tk.id}>
            <RowButton
              onClick={() => onOpen(tk.id)}
              aria-current={on ? "true" : undefined}
              className={cn("flex-col items-stretch gap-1.5", on && "bg-primary/10 hover:bg-primary/10")}
            >
              <span className="flex items-start gap-2">
                <span
                  aria-hidden="true"
                  className={cn("mt-1.5 size-2 shrink-0 rounded-full", on ? "bg-primary" : "bg-transparent")}
                />
                <span className={cn("min-w-0 flex-1", on ? "font-bold" : "font-semibold")}>{tk.subject}</span>
              </span>
              <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pl-4">
                <span className="text-xs text-foreground/70">
                  {tk.number} · {fmtDateTime(tk.updatedAt, locale)}
                </span>
                <Badge variant={genericStatusVariant(tk.status)}>{t(`portal.ticketStatus.${tk.status}`)}</Badge>
              </span>
            </RowButton>
          </li>
        );
      })}
    </ul>
  );
}
